import { pureAlcoholGrams } from '@lbc/shared';

import {
  DRINK_DEFINITIONS,
  describeDrink as describeDrinkInDomain,
  GLOBAL_DEFAULT_BEER_ABV,
  isBeerVolume as isBeerVolumeInDomain,
  isCocktailDrink,
} from '../../domain/drinks';

export { GLOBAL_DEFAULT_BEER_ABV };

export interface DrinkPreset {
  id: 'beer-500' | 'beer-330' | 'cocktail' | 'wine-100';
  label: string;
  shortLabel: string;
  volumeLabel: string;
  volumeMl: number;
  abv: number;
  estimatedKilocalories: number;
}

export type CounterDrinkId = 'beer' | 'wine' | 'cocktail';

export interface CounterDrink extends Omit<DrinkPreset, 'id'> {
  id: CounterDrinkId;
}

export const DRINK_CALORIE_DEFAULTS = {
  ethanolKilocaloriesPerGram: 7,
  beerNonAlcoholKilocaloriesPer100Ml: 14,
} as const;

export function createDrinkPresets(beerAbv: number): readonly DrinkPreset[] {
  const presets: Omit<DrinkPreset, 'estimatedKilocalories'>[] = [
    DRINK_DEFINITIONS.beer[500],
    DRINK_DEFINITIONS.beer[330],
    DRINK_DEFINITIONS.cocktail,
    DRINK_DEFINITIONS.wine,
  ].map((definition) => ({
    id: definition.id,
    label: definition.label,
    shortLabel: definition.shortLabel,
    volumeLabel: definition.volumeLabel,
    volumeMl: definition.volumeMl,
    abv: definition.abv ?? beerAbv,
  }));

  return presets.map((preset) => ({
    ...preset,
    estimatedKilocalories: estimateDrinkKilocalories(preset),
  }));
}

/** The compact counter has one tile per drink category. Measurements are kept per tile in the UI. */
export function createCounterDrinks(beerAbv: number): readonly CounterDrink[] {
  return [
    {
      id: 'beer',
      label: '0,5 Liter Bier',
      shortLabel: 'Bier',
      volumeLabel: '0,5 l',
      volumeMl: 500,
      abv: beerAbv,
      estimatedKilocalories: estimateDrinkKilocalories({ volumeMl: 500, abv: beerAbv }),
    },
    {
      id: 'wine',
      label: '0,1 Liter Wein',
      shortLabel: 'Wein',
      volumeLabel: '0,1 l',
      volumeMl: 100,
      abv: 12,
      estimatedKilocalories: estimateDrinkKilocalories({ volumeMl: 100, abv: 12 }),
    },
    {
      id: 'cocktail',
      label: 'Cocktail mit 6 cl Spirituose',
      shortLabel: 'Cocktail',
      volumeLabel: '6 cl',
      volumeMl: 60,
      abv: 40,
      estimatedKilocalories: estimateDrinkKilocalories({ volumeMl: 60, abv: 40 }),
    },
  ];
}

export function describeDrink(event: { abv: number; volumeMl: number }, beerAbv: number): string {
  return describeDrinkInDomain(event, beerAbv);
}

export function estimateDrinkKilocalories(event: { abv: number; volumeMl: number }): number {
  const alcoholKilocalories =
    pureAlcoholGrams(event.volumeMl, event.abv) * DRINK_CALORIE_DEFAULTS.ethanolKilocaloriesPerGram;
  const nonAlcoholKilocalories = isBeerVolume(event.volumeMl)
    ? (event.volumeMl / 100) * DRINK_CALORIE_DEFAULTS.beerNonAlcoholKilocaloriesPer100Ml
    : 0;

  return Math.round(alcoholKilocalories + nonAlcoholKilocalories);
}

export function isCocktailPreset(event: { abv: number; volumeMl: number }): boolean {
  return isCocktailDrink(event);
}

function isBeerVolume(volumeMl: number): boolean {
  return isBeerVolumeInDomain(volumeMl);
}
